import { useParams, Navigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { ReactElement } from 'react';
import { getNewsBySlug, newsItems } from '../../mocks/news';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

export default function NewsDetail() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();
  const { ref, isVisible } = useScrollAnimation(0.1);

  const newsItem = slug ? getNewsBySlug(slug) : undefined;

  if (!newsItem) {
    return <Navigate to="/news" replace />;
  }

  // Get related news (same category or recent)
  const relatedNews = newsItems
    .filter(item => item.id !== newsItem.id && item.category === newsItem.category)
    .slice(0, 3);

  if (relatedNews.length < 3) {
    const additionalNews = newsItems
      .filter(item => item.id !== newsItem.id && !relatedNews.find(r => r.id === item.id))
      .slice(0, 3 - relatedNews.length);
    relatedNews.push(...additionalNews);
  }

  const renderContent = (content: string): ReactElement[] => {
    const lines = content.split('\n');
    let inList = false;
    let listItems: string[] = [];
    const elements: ReactElement[] = [];
    let key = 0;

    const flushList = () => {
      if (inList && listItems.length > 0) {
        elements.push(
          <ul key={`list-${key++}`} className="list-disc list-inside space-y-2 mb-4 font-source text-gray-600 leading-relaxed">
            {listItems.map((item, idx) => (
              <li key={idx}>{item.replace(/^- /, '')}</li>
            ))}
          </ul>
        );
        listItems = [];
        inList = false;
      }
    };

    lines.forEach((line) => {
      const trimmed = line.trim();
      
      if (trimmed === '') {
        flushList();
        return;
      }

      if (trimmed.startsWith('**') && trimmed.endsWith('**')) {
        flushList();
        elements.push(
          <h3 key={`h-${key++}`} className="font-playfair text-xl font-bold text-navy mt-8 mb-4">
            {trimmed.replace(/\*\*/g, '')}
          </h3>
        );
      } else if (trimmed.startsWith('- ')) {
        inList = true;
        listItems.push(trimmed);
      } else {
        flushList();
        elements.push(
          <p key={`p-${key++}`} className="font-source text-gray-600 leading-relaxed mb-4">
            {trimmed}
          </p>
        );
      }
    });

    flushList();
    return elements;
  };

  return (
    <div className="w-full">
      {/* Hero Article */}
      <section className="relative w-full min-h-[60vh] md:min-h-[70vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={newsItem.imageUrl}
            alt={newsItem.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/60 to-transparent" />
        </div>

        <div className="absolute top-24 right-16 w-24 h-24 border border-gold/30 rotate-45 rounded-lg pointer-events-none" />
        <svg className="absolute bottom-32 left-8 w-20 h-20 opacity-[0.1] pointer-events-none" viewBox="0 0 80 80">
          <circle cx="40" cy="40" r="35" fill="none" stroke="#F7941E" strokeWidth="1" strokeDasharray="4 4" />
        </svg>

        <div className="relative z-10 w-full px-4 md:px-6 lg:px-10 py-16 md:py-20 lg:py-24 pt-32 md:pt-40">
          <div className="max-w-4xl mx-auto">
            <div className="flex wrap items-center gap-3 mb-6">
              <span className="px-4 py-1.5 bg-gold text-navy text-sm font-semibold font-source rounded-full">
                {newsItem.category}
              </span>
              <span className="flex items-center gap-2 text-white/80 font-source text-sm">
                <i className="ri-calendar-line" />
                {newsItem.date}
              </span>
            </div>
            <h1 className="font-playfair text-3xl md:text-4xl lg:text-5xl text-white leading-tight mb-6">
              {newsItem.title}
            </h1>
            <p className="font-source text-lg text-white/80 leading-relaxed max-w-2xl">
              {newsItem.excerpt}
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section ref={ref} className="relative w-full px-4 md:px-6 lg:px-10 py-12 md:py-16 bg-white overflow-hidden">
        <svg className="absolute inset-0 w-full h-full opacity-[0.03] pointer-events-none" viewBox="0 0 1440 600" preserveAspectRatio="xMidYMid slice">
          <circle cx="200" cy="100" r="300" fill="none" stroke="#1A2460" strokeWidth="2" />
          <circle cx="1200" cy="500" r="350" fill="none" stroke="#F7941E" strokeWidth="1.5" />
        </svg>

        <div className="relative z-10 max-w-3xl mx-auto">
          <div className={`transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            {/* Article Content */}
            <div className="prose prose-lg max-w-none">
              {renderContent(newsItem.content)}
            </div>

            {/* Share Section */}
            <div className="mt-12 pt-8 border-t border-gray-100">
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                <div>
                  <h4 className="font-source text-sm font-semibold text-navy mb-2">
                    {t('news_share', 'Partager cet article')}
                  </h4>
                  <div className="flex items-center gap-3">
                    <a href="#" className="w-10 h-10 rounded-lg bg-navy/10 flex items-center justify-center text-navy hover:bg-navy hover:text-white transition-colors">
                      <i className="ri-facebook-fill" />
                    </a>
                    <a href="#" className="w-10 h-10 rounded-lg bg-navy/10 flex items-center justify-center text-navy hover:bg-navy hover:text-white transition-colors">
                      <i className="ri-twitter-x-fill" />
                    </a>
                    <a href="#" className="w-10 h-10 rounded-lg bg-navy/10 flex items-center justify-center text-navy hover:bg-navy hover:text-white transition-colors">
                      <i className="ri-linkedin-fill" />
                    </a>
                    <a href="#" className="w-10 h-10 rounded-lg bg-navy/10 flex items-center justify-center text-navy hover:bg-navy hover:text-white transition-colors">
                      <i className="ri-whatsapp-line" />
                    </a>
                  </div>
                </div>
                <Link
                  to="/news"
                  className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-semibold text-navy border border-navy/20 rounded-lg hover:border-gold hover:text-gold transition-colors"
                >
                  <i className="ri-arrow-left-line" />
                  {t('news_back_to_list', 'Retour aux actualités')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related News */}
      <section className="relative w-full px-4 md:px-6 lg:px-10 py-16 md:py-20 bg-offWhite overflow-hidden">
        <svg className="absolute inset-0 w-full h-full opacity-[0.04] pointer-events-none" viewBox="0 0 1440 600" preserveAspectRatio="xMidYMid slice">
          <circle cx="150" cy="500" r="350" fill="none" stroke="#1A2460" strokeWidth="2" />
          <circle cx="1250" cy="100" r="300" fill="none" stroke="#F7941E" strokeWidth="1.5" />
        </svg>

        <div className="relative z-10 max-w-6xl mx-auto">
          <h2 className="font-playfair text-2xl md:text-3xl text-navy mb-8 text-center">
            {t('news_related', 'Articles similaires')}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {relatedNews.map((item) => (
              <article
                key={item.id}
                className="group relative bg-white rounded-xl overflow-hidden border border-gray-100 transition-all duration-300 hover:-translate-y-1"
                style={{ boxShadow: '0 4px 20px rgba(26,26,46,0.05)' }}
              >
                <div className="relative h-40 overflow-hidden">
                  <img
                    src={item.imageUrl}
                    alt={item.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy/30 via-transparent to-transparent" />
                </div>

                <div className="p-5">
                  <p className="font-source text-xs text-gray-400 tracking-widest uppercase mb-2">
                    {item.date}
                  </p>
                  <h3 className="font-playfair text-base font-bold text-navy mb-2 leading-snug group-hover:text-gold transition-colors duration-300 line-clamp-2">
                    {item.title}
                  </h3>
                  <Link
                    to={`/news/${item.slug}`}
                    className="inline-flex items-center gap-1 font-source text-sm font-semibold text-navy group-hover:text-gold transition-colors"
                  >
                    {t('news_read_more')}
                    <i className="ri-arrow-right-line" />
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
